import { motion } from "framer-motion";

/* Editorial before/after occupancy chart
   - Two animated bars: before (grey) and after (gold)
   - Large lift callout (+133 %)
   - Matches premium dark/gold aesthetic
*/
export default function BeforeAfterChart({ data }) {
  const { before, after, unit = "%", lift, metric, period, note } = data;
  const maxScale = Math.max(before, after);
  const beforePct = (before / maxScale) * 100;
  const afterPct = (after / maxScale) * 100;

  return (
    <section
      className="bg-[#0a0a0a] text-white py-16 sm:py-24 mt-16 sm:mt-24 relative overflow-hidden"
      data-testid="before-after-chart"
    >
      {/* Soft gold spot */}
      <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] glow-gold pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Chapter header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12 sm:mb-16"
        >
          <div>
            <p className="overline mb-4 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
              <span className="inline-block w-8 sm:w-12 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Før &amp; efter
            </p>
            <h3
              className="font-serif text-[1.75rem] sm:text-4xl lg:text-5xl font-light leading-[1.1] tracking-tight text-white max-w-2xl"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Kalenderen der gik fra{" "}
              <em className="italic text-[#D4AF37]">stille</em> til{" "}
              <em className="italic text-[#D4AF37]">fuld</em>.
            </h3>
          </div>
          <div className="text-[11px] tracking-[0.24em] uppercase text-white/50 sm:text-right shrink-0">
            {period}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — Bars */}
          <div className="lg:col-span-8 space-y-10 sm:space-y-14">
            {/* Before bar */}
            <div data-testid="chart-bar-before">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-[11px] tracking-[0.28em] uppercase text-white/50">
                  Før overtagelse
                </span>
                <motion.span
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  className="font-serif text-white/60 text-lg sm:text-xl"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  ~{before}
                  {unit}
                </motion.span>
              </div>
              <div className="relative h-3 sm:h-4 bg-white/[0.06] overflow-hidden rounded-full">
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: beforePct / 100 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                  className="absolute inset-y-0 left-0 w-full bg-white/25 origin-left rounded-full"
                />
              </div>
            </div>

            {/* After bar */}
            <div data-testid="chart-bar-after">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37]">
                  Efter overtagelse
                </span>
                <motion.span
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 1.6 }}
                  className="font-serif text-white text-xl sm:text-2xl"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  ~{after}
                  {unit}
                </motion.span>
              </div>
              <div className="relative h-3 sm:h-4 bg-white/[0.06] overflow-hidden rounded-full">
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: afterPct / 100 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.8 }}
                  className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-[#D4AF37] to-[#F0CD70] origin-left rounded-full"
                />
              </div>
            </div>

            {/* Metric + note */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-6 border-t border-white/10">
              <div className="text-[11px] tracking-[0.24em] uppercase text-white/60">
                Måler: {metric}
              </div>
              {note && (
                <div className="text-[11px] text-white/40 sm:ml-auto max-w-xs sm:text-right leading-relaxed">
                  {note}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Big lift number */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-white/10 text-center lg:text-left"
            data-testid="chart-lift"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] mb-3">
              Belægnings-løft
            </div>
            <div
              className="font-serif text-[4.5rem] sm:text-[6rem] lg:text-[7rem] leading-none font-light text-white"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {lift}
            </div>
            <div className="mt-3 text-sm text-white/60 font-light leading-relaxed max-w-xs mx-auto lg:mx-0">
              Fra en næsten stille kalender til{" "}
              <em className="italic text-white">fast belægning</em> — uden ejerens
              indblanding.
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
